
import { useState, useEffect } from "react";
import Header from "@/components/header/ModernHeader";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/SEO/Breadcrumbs";
import StructuredData from "@/components/SEO/StructuredData";
import { useSEO } from "@/hooks/useSEO";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { generateWebsiteSchema } from "@/utils/seoUtils";
import { useProductsData } from "@/hooks/useProductsData";
import ProductsHeader from "@/components/Products/ProductsHeader";
import LazyProductGrid, { ViewDensity } from "@/components/Products/LazyProductGrid";
import ProductsLoading from "@/components/Products/ProductsLoading";
import AdvancedLuxuryFilters from "@/components/Products/AdvancedLuxuryFilters";
import FloatingActions from "@/components/Products/FloatingActions";
import RecentlyViewedBar from "@/components/Products/RecentlyViewedBar";
import { Button } from "@/components/ui/button";
import { SlidersHorizontal } from "lucide-react";

const OptimizedProducts = () => {
  const { data: siteSettings } = useSiteSettings();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("name");
  const [viewDensity, setViewDensity] = useState<ViewDensity>('comfortable');
  const [showFilters, setShowFilters] = useState(true);

  // Load saved preferences
  useEffect(() => {
    const savedDensity = localStorage.getItem('product-view-density') as ViewDensity;
    if (savedDensity && ['comfortable', 'compact', 'dense'].includes(savedDensity)) {
      setViewDensity(savedDensity);
    }
  }, []);

  const handleDensityChange = (density: ViewDensity) => {
    setViewDensity(density);
    localStorage.setItem('product-view-density', density);
  };

  useSEO({
    pageType: 'page',
    fallbackTitle: 'Premium Products - Luxury Collection | VRAM Store',
    fallbackDescription: 'Browse our optimized collection of premium electronics and luxury items. Fast loading, best prices, quality guaranteed.',
    customSchema: siteSettings ? generateWebsiteSchema(siteSettings) : null
  });

  const { products, isLoading, categories } = useProductsData(searchTerm, selectedCategory, sortBy);

  const breadcrumbItems = [
    { label: 'Optimized Collection', active: true }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <ProductsLoading />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-200">
      <Header />
      
      <StructuredData data={siteSettings ? generateWebsiteSchema(siteSettings) : null} id="website-schema" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs items={breadcrumbItems} />
        
        <ProductsHeader 
          density={viewDensity}
          onDensityChange={handleDensityChange}
          productCount={products?.length}
        />

        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2 lg:hidden"
            >
              <SlidersHorizontal className="h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
            
            {products && (
              <div className="text-sm text-muted-foreground font-medium">
                <span className="text-primary font-bold">{products.length}</span> premium items
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-8 relative">
          <AdvancedLuxuryFilters
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            sortBy={sortBy}
            setSortBy={setSortBy}
            categories={categories}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
          />

          <div className="flex-1">
            <LazyProductGrid 
              products={products} 
              density={viewDensity}
              useVirtualization={true}
              virtualizationThreshold={30}
              itemsPerPage={20}
            />
          </div>
        </div>
      </div>

      <FloatingActions />
      <RecentlyViewedBar />
      <Footer />
    </div>
  );
};

export default OptimizedProducts;
